import { interpolateString } from 'd3-interpolate';
import { BaseType, Selection, select } from 'd3-selection';

type D3AnimationData = {
  enabled: boolean;
  delay: number;
  duration: number;
};

type D3CallbackData<T = unknown> = {
  click?: (event: MouseEvent, obj: T, type: string, target: EventTarget) => void;
  enter?: (event: MouseEvent, obj: T, type: string, target: EventTarget) => void;
  leave?: (event: MouseEvent, obj: T, type: string, target: EventTarget) => void;
  move?: (event: MouseEvent, obj: T, type: string, target: EventTarget) => void;
};

// D3RenderData provides animate and callbacks generically
type D3RenderData<T> = {
  animate?: D3AnimationWrap<T>;
  callbacks?: D3CallbackData<T>;
};

// RenderableWrapper combines T with D3RenderData<T>
type D3RenderWrap<T> = T & D3RenderData<T>;
type D3AnimationWrap<T> = Partial<T> & D3AnimationData;

export type ImageData = {
  x: number;
  y: number;
  width: number;
  height: number;
  href?: string;
  transform?: string;
  [key: string]: unknown;
};

export type LineData = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  color: string;
  width: number;
  lineDash?: string | number[];
  transform?: string;
  crispEdges?: boolean;
  [key: string]: unknown;
};

export type RectData = {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDash?: number[];
  transform?: string;
  crispEdges?: boolean;
  [key: string]: unknown;
};

export type CircleData = {
  x: number;
  y: number;
  color: string;
  radius: number;
  stroke?: string;
  strokeWidth?: number;
  strokeDash?: number[];
  transform?: string;
  crispEdges?: boolean;
  [key: string]: unknown;
};

export type PathData = {
  x?: number;
  y?: number;
  path: string;
  pathFn?: (node: unknown) => (time: number) => string;
  color: string;
  width: number;
  transform?: string;
  crispEdges?: boolean;
  [key: string]: unknown;
};

export type PathFilledData = {
  x?: number;
  y?: number;
  path: string;
  pathFn?: (node: unknown) => (time: number) => string;
  color: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDash?: number[];
  transform?: string;
  crispEdges?: boolean;
  [key: string]: unknown;
};

export type TextData = {
  text: string;
  x: number;
  y: number;
  dx?: string | number;
  dy?: string | number;
  color: string;
  anchor?: string;
  fontSize?: number;
  fontStyle?: string;
  fontWeight?: string;
  fontFamily?: string;
  letterSpacing?: number;
  transform?: string;
  [key: string]: unknown;
};

export type GradientData = {
  id: string;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  stops: GradientStopData[];
};

export type GradientStopData = {
  offset: string | number;
  color: string;
};

// Use the wrapper with D3ImageData
export type D3ImageData = D3RenderWrap<ImageData>;
export type D3LineData = D3RenderWrap<LineData>;
export type D3RectData = D3RenderWrap<RectData>;
export type D3CircleData = D3RenderWrap<CircleData>;
export type D3PathData = D3RenderWrap<PathData>;
export type D3PathFilledData = D3RenderWrap<PathFilledData>;
export type D3TextData = D3RenderWrap<TextData>;
export type D3GradientData = D3RenderWrap<GradientData>;

type NodeSelection<D> = Selection<BaseType, D3RenderWrap<D>, BaseType, unknown>;

const animationDelay = (obj: { animate?: D3AnimationData }) => (obj.animate?.enabled && obj.animate.delay ? obj.animate.delay : 0);
const animationDuration = (obj: { animate?: D3AnimationData }) => (obj.animate?.enabled && obj.animate.duration ? obj.animate.duration : 0);

const applyTransition = <D extends D3RenderData<D>>(selection: NodeSelection<D>, nodeIdx: number, nodeLen: number, callback?: () => void) => {
  let activeAnimations = 0;

  const transition = selection
    .transition()
    .delay((obj) => {
      const delay = obj.animate?.enabled && obj.animate.delay ? obj.animate.delay : 0;
      const duration = obj.animate?.enabled && obj.animate.duration ? obj.animate.duration : 0;
      const maxDelay = duration / nodeLen;

      // console.log('delay', nodeIdx, delay, 'max', Math.min(delay, maxDelay));
      return nodeIdx * delay; // Math.min(delay, maxDelay);
    })
    .duration((obj) => {
      const delay = obj.animate?.enabled && obj.animate.delay ? obj.animate.delay : 0;
      const duration = obj.animate?.enabled && obj.animate.duration ? obj.animate.duration : 0;
      const maxDuration = duration - delay * nodeLen;

      // console.log('duration', nodeIdx, duration, 'max', maxDuration);
      return duration; // Math.max(delay, duration - delay * nodeLen);
    })
    .on('start', () => activeAnimations++)
    .on('end', () => {
      activeAnimations--;
      if (activeAnimations === 0 && callback) callback();
    });

  return transition;
};

const applyCallbacks = <D extends D3RenderData<D>>(selection: NodeSelection<D>) => {
  selection
    .on('click', (event: MouseEvent, obj: unknown) => callbackFn(event, obj, 'click', event.currentTarget))
    .on('mouseenter', (event: MouseEvent, obj: unknown) => callbackFn(event, obj, 'enter', event.currentTarget))
    .on('mouseleave', (event: MouseEvent, obj: unknown) => callbackFn(event, obj, 'leave', event.currentTarget))
    .on('mousemove', (event: MouseEvent, obj: unknown) => callbackFn(event, obj, 'move', event.currentTarget));
};

const attrIfAnimated = (obj: { animate?: D3AnimationData }, node: any, name: string, attr?: string, unit = '') => {
  // no animation information available
  if (!obj.animate || !obj.animate.enabled) return;

  if (obj.animate[name] === undefined || (typeof obj.animate[name] === 'number' && Number.isNaN(obj.animate[name]))) {
    return;
  }

  node.attr(attr || name, obj.animate[name] + unit);
};

const attrIfStatic = (enter: boolean, obj: any, node: any, name: string, attr?: string, unit = '') => {
  // remove attr
  if (obj[name] === undefined || (typeof obj[name] === 'number' && Number.isNaN(obj[name]))) {
    node.attr(attr || name, null);
    return;
  }

  // this attr will be animated
  if (!enter && obj.animate && obj.animate.enabled && obj.animate[name] !== undefined) return;

  // try to use the animation result
  if (obj.animate && !obj.animate.enabled && obj.animate[name] !== undefined) {
    node.attr(attr || name, obj.animate[name] + unit);
  } else {
    node.attr(attr || name, obj[name] + unit);
  }
};

const attrIfValid = <D extends Record<string, unknown>>(obj: any, node: NodeSelection<D>, name: string, attr?: string, unit = '') => {
  // remove attr
  if (obj[name] === undefined || (typeof obj[name] === 'number' && Number.isNaN(obj[name]))) {
    node.attr(attr || name, null);
    return;
  }

  // try to use the animation result
  node.attr(attr || name, obj[name] + unit);
};

const callbackFn = (event: MouseEvent, obj: { callbacks?: D3CallbackData }, name: string, ...arg) => {
  if (!obj.callbacks) return;
  if (!obj.callbacks[name]) return;
  obj.callbacks[name](event, obj, ...arg);
};

const renderSelection = <D extends Record<string, unknown>>(
  element: NodeSelection<D>,
  data: D[],
  applyAttributes: (attrSelection: NodeSelection<D>, enter?: boolean) => void,
  domType: string,
  selector?: string,
  interrupt = true,
) => {
  const selection = (selector ? element.select(selector) : element).selectAll(domType).data(data);

  // pause active animations
  if (interrupt) selection.interrupt();

  // create new elements
  selection.enter().append(domType).call(applyAttributes, true).call(applyCallbacks);

  // update existing elements
  selection.call(applyAttributes);

  // remove expired elements
  selection.exit().remove();
};

// D3 Render Functions

export const d3renderImage = <D extends D3ImageData>(element: NodeSelection<D>, data: D[], selector?: string, callback?: () => void) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      if (dataObj.callbacks?.click) {
        node.style('cursor', 'pointer');
        node.style('pointer-events', 'all');
      }

      attrIfValid(dataObj, node, 'href');

      attrIfStatic(enter, dataObj, node, 'x');
      attrIfStatic(enter, dataObj, node, 'y');
      attrIfStatic(enter, dataObj, node, 'width');
      attrIfStatic(enter, dataObj, node, 'height');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x');
      attrIfAnimated(dataObj, transition, 'y');
      attrIfAnimated(dataObj, transition, 'width');
      attrIfAnimated(dataObj, transition, 'height');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element, data, applyAttributes, 'image', selector);
};

export const d3renderLine = <D extends D3LineData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      node.style('shape-rendering', 'crispedges');

      attrIfStatic(enter, dataObj, node, 'x1');
      attrIfStatic(enter, dataObj, node, 'x2');
      attrIfStatic(enter, dataObj, node, 'y1');
      attrIfStatic(enter, dataObj, node, 'y2');
      attrIfStatic(enter, dataObj, node, 'color', 'stroke');
      attrIfStatic(enter, dataObj, node, 'width', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'lineDash', 'stroke-dasharray');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x1');
      attrIfAnimated(dataObj, transition, 'x2');
      attrIfAnimated(dataObj, transition, 'y1');
      attrIfAnimated(dataObj, transition, 'y2');
      attrIfAnimated(dataObj, transition, 'color', 'stroke');
      attrIfAnimated(dataObj, transition, 'width', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'lineDash', 'stroke-dasharray');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'line', selector);
};

export const d3renderCircle = <D extends D3CircleData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      if (dataObj.crispEdges) {
        node.style('shape-rendering', 'crispedges');
      }

      attrIfStatic(enter, dataObj, node, 'x', 'cx');
      attrIfStatic(enter, dataObj, node, 'y', 'cy');
      attrIfStatic(enter, dataObj, node, 'color', 'fill');
      attrIfStatic(enter, dataObj, node, 'radius', 'r');
      attrIfStatic(enter, dataObj, node, 'stroke');
      attrIfStatic(enter, dataObj, node, 'strokeWidth', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'strokeDash', 'stroke-dasharray');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x', 'cx');
      attrIfAnimated(dataObj, transition, 'y', 'cy');
      attrIfAnimated(dataObj, transition, 'color', 'fill');
      attrIfAnimated(dataObj, transition, 'radius', 'r');
      attrIfAnimated(dataObj, transition, 'stroke');
      attrIfAnimated(dataObj, transition, 'strokeWidth', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'strokeDash', 'stroke-dasharray');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'circle', selector);
};

export const d3renderRect = <D extends D3RectData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      // .attrTween('d', (obj1: any) => obj1.pathFn(nodes[idx]));

      // obj.path = rectToPath(obj.x, obj.y, obj.width, obj.height, { topRight: 16, bottomRight: 16 });
      // obj.animate.path = rectToPath(obj.animate.x, obj.animate.y, obj.animate.width, obj.animate.height, { topRight: 16, bottomRight: 16 });

      // obj.pathFn = (node: any) => {
      //   const intFn = interpolateString(obj.path, obj.animate.path);
      //   return (time) => intFn(time);
      // };

      node.style('shape-rendering', 'crispedges');

      if (dataObj.callbacks?.click) {
        node.style('cursor', 'pointer');
        node.style('pointer-events', 'all');
      }

      attrIfStatic(enter, dataObj, node, 'x');
      attrIfStatic(enter, dataObj, node, 'y');
      attrIfStatic(enter, dataObj, node, 'width');
      attrIfStatic(enter, dataObj, node, 'height');
      attrIfStatic(enter, dataObj, node, 'color', 'fill');
      attrIfStatic(enter, dataObj, node, 'stroke');
      attrIfStatic(enter, dataObj, node, 'strokeWidth', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'strokeDash', 'stroke-dasharray');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x');
      attrIfAnimated(dataObj, transition, 'y');
      attrIfAnimated(dataObj, transition, 'width');
      attrIfAnimated(dataObj, transition, 'height');
      attrIfAnimated(dataObj, transition, 'color', 'fill');
      attrIfAnimated(dataObj, transition, 'stroke');
      attrIfAnimated(dataObj, transition, 'strokeWidth', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'strokeDash', 'stroke-dasharray');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'rect', selector);
};

export const d3renderPath = <D extends D3PathData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      let activeAnimations = 0;
      transition.on('start', () => activeAnimations++);

      transition.on('end', () => {
        node.attr('stroke-dasharray', null);
        node.attr('stroke-dashoffset', null);

        activeAnimations--;
        if (activeAnimations === 0 && callback) callback();
      });

      node.attr('fill', 'none');

      if (dataObj.crispEdges) {
        node.style('shape-rendering', 'crispedges');
      }

      attrIfStatic(enter, dataObj, node, 'x');
      attrIfStatic(enter, dataObj, node, 'y');
      attrIfStatic(true, dataObj, node, 'path', 'd');
      attrIfStatic(enter, dataObj, node, 'color', 'stroke');
      attrIfStatic(enter, dataObj, node, 'width', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x');
      attrIfAnimated(dataObj, transition, 'y');
      attrIfAnimated(dataObj, transition, 'path', 'd');
      attrIfAnimated(dataObj, transition, 'color', 'stroke');
      attrIfAnimated(dataObj, transition, 'width', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'transform');

      const pathNode = node.node() as SVGGeometryElement;
      if (pathNode && dataObj.animate?.enabled) {
        const lineLength = Math.ceil(pathNode.getTotalLength());
        node.attr('stroke-dashoffset', lineLength);
        node.attr('stroke-dasharray', `${lineLength} ${lineLength}`);

        transition.attr('stroke-dashoffset', 0);
      } else {
        node.attr('stroke-dasharray', null);
        node.attr('stroke-dashoffset', null);
      }
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'path', selector);
};

export const d3renderPathFilled = <D extends D3PathFilledData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      if (enter) {
        // @ts-ignore
        nodes[idx].prev = dataObj.prev;
      }

      if (dataObj.crispEdges) {
        node.style('shape-rendering', 'crispedges');
      }

      if (dataObj.callbacks?.click) {
        node.style('cursor', 'pointer');
        node.style('pointer-events', 'all');
      }

      if (dataObj.debug) {
        node.attr('fill-opacity', '0');
      }

      if (dataObj.pathFn) {
        // @ts-ignore
        transition.attrTween('d', (tweenObj) => tweenObj.pathFn(nodes[idx]));
      }

      attrIfStatic(enter, dataObj, node, 'x');
      attrIfStatic(enter, dataObj, node, 'y');
      attrIfStatic(enter, dataObj, node, 'path', 'd');
      attrIfStatic(enter, dataObj, node, 'color', 'fill');
      attrIfStatic(enter, dataObj, node, 'stroke', 'stroke');
      attrIfStatic(enter, dataObj, node, 'width', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'strokeWidth', 'stroke-width');
      attrIfStatic(enter, dataObj, node, 'strokeDash', 'stroke-dasharray');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x');
      attrIfAnimated(dataObj, transition, 'y');
      attrIfAnimated(dataObj, transition, 'path', 'd');
      attrIfAnimated(dataObj, transition, 'color', 'fill');
      attrIfAnimated(dataObj, transition, 'stroke', 'stroke');
      attrIfAnimated(dataObj, transition, 'width', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'strokeWidth', 'stroke-width');
      attrIfAnimated(dataObj, transition, 'strokeDash', 'stroke-dasharray');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'path', selector, false);
};

export const d3renderText = <D extends D3TextData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);
      const transition = applyTransition(node, idx, nodes.length, callback);

      if (dataObj.callbacks?.click) {
        node.style('cursor', 'pointer');
        node.style('pointer-events', 'all');
      }

      node.text((obj) => obj.text);

      attrIfStatic(enter, dataObj, node, 'x');
      attrIfStatic(enter, dataObj, node, 'y');
      attrIfStatic(enter, dataObj, node, 'dx');
      attrIfStatic(enter, dataObj, node, 'dy');
      attrIfStatic(enter, dataObj, node, 'color', 'fill');
      attrIfStatic(enter, dataObj, node, 'anchor', 'text-anchor');
      attrIfStatic(enter, dataObj, node, 'fontSize', 'font-size', 'px');
      attrIfStatic(enter, dataObj, node, 'fontStyle', 'font-style');
      attrIfStatic(enter, dataObj, node, 'fontWeight', 'font-weight');
      attrIfStatic(enter, dataObj, node, 'fontFamily', 'font-family');
      attrIfStatic(enter, dataObj, node, 'letterSpacing', 'letter-spacing');
      attrIfStatic(enter, dataObj, node, 'transform');

      attrIfAnimated(dataObj, transition, 'x');
      attrIfAnimated(dataObj, transition, 'y');
      attrIfAnimated(dataObj, transition, 'dx');
      attrIfAnimated(dataObj, transition, 'dy');
      attrIfAnimated(dataObj, transition, 'color', 'fill');
      attrIfAnimated(dataObj, transition, 'anchor', 'text-anchor');
      attrIfAnimated(dataObj, transition, 'fontSize', 'font-size', 'px');
      attrIfAnimated(dataObj, transition, 'fontStyle', 'font-style');
      attrIfAnimated(dataObj, transition, 'fontWeight', 'font-weight');
      attrIfAnimated(dataObj, transition, 'fontFamily', 'font-family');
      attrIfAnimated(dataObj, transition, 'letterSpacing', 'letter-spacing');
      attrIfAnimated(dataObj, transition, 'transform');
    });
  };

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'text', selector);
};

export const d3renderGradient = <D extends D3GradientData>(
  element: Selection<BaseType, unknown, null, undefined>,
  data: D[],
  selector?: string,
  callback?: () => void,
) => {
  const applyStopAttributes = (stopSelection: NodeSelection<GradientStopData>, enter = false) => {
    stopSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, GradientStopData>(nodes[idx]);

      attrIfStatic(enter, dataObj, node, 'offset');
      attrIfStatic(enter, dataObj, node, 'color', 'stop-color');
    });
  };

  const applyAttributes = (attrSelection: NodeSelection<D>, enter = false) => {
    attrSelection.each((dataObj, idx, nodes) => {
      const node = select<BaseType, D>(nodes[idx]);

      node.attr('id', (obj) => obj.id);
      node.attr('gradientUnits', 'userSpaceOnUse');

      attrIfStatic(enter, dataObj, node, 'x1');
      attrIfStatic(enter, dataObj, node, 'x2');
      attrIfStatic(enter, dataObj, node, 'y1');
      attrIfStatic(enter, dataObj, node, 'y2');

      renderSelection(node as unknown as NodeSelection<GradientStopData>, dataObj.stops, applyStopAttributes, 'stop', null, false);
    });
  };

  /*
  <linearGradient id="line-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="360" x2="0" y2="0">
    <stop offset="0%" stop-color="blue"></stop>
    <stop offset="100%" stop-color="red"></stop>
  </linearGradient>
  */

  renderSelection(element as NodeSelection<D>, data, applyAttributes, 'linearGradient', selector);
};
